import { PROXIMITY_SERVICE_UUID } from '../constants';
import type { ProximityBleBridge } from './bleBridge';

export type BleAdvertiserOptions = {
  bridge: ProximityBleBridge;
  /** Peer id encoded in the Lynx advertisement (`Lynx|<userId>`). */
  localPeerId: string;
  serviceUuid?: string;
  /** When the session's event stops being live (epoch ms); native will not advertise or restore past it. */
  liveUntilMs?: number | null;
  /** Private beacon: rotating ids and sealed GPS (docs/beacon-privacy.md). */
  beaconPrivacy?: boolean;
  /** Shadow test of the private beacon beside the legacy one (logging only). */
  beaconShadow?: boolean;
};

/**
 * Starts/stops BLE advertising so other Prii devices can discover this device.
 */
export class BleAdvertiser {
  private readonly bridge: ProximityBleBridge;
  private readonly localPeerId: string;
  private readonly serviceUuid: string;
  private readonly liveUntilMs: number | null;
  private readonly beaconPrivacy: boolean;
  private readonly beaconShadow: boolean;
  private active = false;

  constructor(options: BleAdvertiserOptions) {
    this.bridge = options.bridge;
    this.localPeerId = options.localPeerId;
    this.serviceUuid = options.serviceUuid ?? PROXIMITY_SERVICE_UUID;
    this.liveUntilMs = options.liveUntilMs ?? null;
    this.beaconPrivacy = options.beaconPrivacy ?? false;
    this.beaconShadow = options.beaconShadow ?? false;
  }

  get isActive(): boolean {
    return this.active;
  }

  async start(): Promise<void> {
    if (this.active) {
      return;
    }
    await this.bridge.startAdvertising({
      serviceUuid: this.serviceUuid,
      userId: this.localPeerId,
      liveUntilMs: this.liveUntilMs,
      beaconPrivacy: this.beaconPrivacy,
      beaconShadow: this.beaconShadow,
    });
    this.active = true;
  }

  async stop(): Promise<void> {
    if (!this.active) {
      return;
    }
    await this.bridge.stopAdvertising();
    this.active = false;
  }
}
